import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { adminService } from '../../services/adminService';
import {
  FiArrowLeft,
  FiEdit,
  FiMail,
  FiPhone,
  FiMapPin,
  FiUser,
  FiPackage,
  FiCalendar,
  FiDollarSign,
  FiUsers,
  FiTrendingUp,
  FiCheckCircle,
  FiXCircle,
  FiClock
} from 'react-icons/fi';

const MerchantDetailsPage = () => {
  const { merchantId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [merchant, setMerchant] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadMerchantDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  const loadMerchantDetails = async () => {
    try {
      setLoading(true);
      const response = await adminService.getMerchantById(merchantId);
      setMerchant(response.data.merchant);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error loading merchant details:', error);
      // Fallback to empty data
      setMerchant(null);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Merchant Details">
        <LoadingSpinner fullScreen />
      </AdminLayout>
    );
  }

  if (!merchant) {
    return (
      <AdminLayout title="Merchant Details">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FiXCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Merchant Not Found</h2>
          <p className="text-gray-600 mb-6">The merchant you're looking for doesn't exist or has been deleted.</p>
          <button
            onClick={() => navigate('/admin/merchants')}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Back to Merchants
          </button>
        </div>
      </AdminLayout>
    );
  }

  const getStatusBadge = (status) => {
    const badges = {
      active: { bg: 'bg-green-100', text: 'text-green-800', icon: FiCheckCircle },
      inactive: { bg: 'bg-gray-100', text: 'text-gray-800', icon: FiXCircle },
      expired: { bg: 'bg-red-100', text: 'text-red-800', icon: FiClock },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: FiClock }
    };

    const badge = badges[status] || badges.inactive;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-4 h-4" />
        <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
      </span>
    );
  };

  const getPackageBadge = (pkg) => {
    const badges = {
      trial: 'bg-blue-100 text-blue-800',
      base: 'bg-green-100 text-green-800',
      basic_plus: 'bg-indigo-100 text-indigo-800'
    };

    const labels = {
      trial: 'Trial',
      base: 'Base',
      basic_plus: 'Pro'
    };

    return (
      <span className={`px-4 py-2 rounded-full text-sm font-medium ${badges[pkg]}`}>
        {labels[pkg]}
      </span>
    );
  };

  return (
    <AdminLayout title="Merchant Details">
      <div className="space-y-6">
        {/* Header with Back Button and Edit Button */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/admin/merchants')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <FiArrowLeft className="w-5 h-5" />
            <span>Back to Merchants</span>
          </button>
          <button
            onClick={() => navigate(`/admin/merchants/${merchantId}/edit`)}
            className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <FiEdit className="w-4 h-4" />
            <span>Edit Merchant</span>
          </button>
        </div>

        {/* Merchant Info Card */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">{merchant.businessName}</h1>
                <p className="text-indigo-100">@{merchant.username}</p>
              </div>
              <div className="text-right">
                {getStatusBadge(merchant.subscription?.status || 'inactive')}
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Contact Info */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <FiMail className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-900">{merchant.email}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <FiPhone className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-900">{merchant.mobile || 'N/A'}</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <FiMapPin className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <div className="text-gray-900">{merchant.location || 'N/A'}</div>
                      {merchant.address && (
                        <div className="text-sm text-gray-500 mt-1">{merchant.address}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Info */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Business Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <FiUser className="w-5 h-5 text-gray-400" />
                    <div>
                      <div className="text-xs text-gray-500">Owner Name</div>
                      <div className="text-gray-900">{merchant.ownerName || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <FiPackage className="w-5 h-5 text-gray-400" />
                    <div>
                      <div className="text-xs text-gray-500">Registration Number</div>
                      <div className="text-gray-900">{merchant.registrationNumber || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <FiCalendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <div className="text-xs text-gray-500">Joined Date</div>
                      <div className="text-gray-900">
                        {new Date(merchant.createdAt).toLocaleDateString('en-GB', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subscription Info */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Subscription</h3>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-gray-500 mb-2">Package</div>
                    {getPackageBadge(merchant.subscription?.package || 'trial')}
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Start Date</div>
                    <div className="text-gray-900">
                      {merchant.subscription?.startDate
                        ? new Date(merchant.subscription.startDate).toLocaleDateString('en-GB')
                        : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Expiry Date</div>
                    <div className="text-gray-900">
                      {merchant.subscription?.expiryDate
                        ? new Date(merchant.subscription.expiryDate).toLocaleDateString('en-GB')
                        : 'N/A'}
                    </div>
                  </div>
                  {merchant.isFreeTrialUser && (
                    <div className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                      Free Trial User
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Debtors</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalDebtors}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <FiUsers className="w-8 h-8 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Outstanding</p>
                  <p className="text-2xl font-bold text-red-600">BHD {stats.totalOutstanding.toFixed(3)}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <FiTrendingUp className="w-8 h-8 text-red-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Paid</p>
                  <p className="text-2xl font-bold text-green-600">BHD {stats.totalPaid.toFixed(3)}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <FiDollarSign className="w-8 h-8 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Transactions</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalTransactions}</p>
                </div>
                <div className="p-3 bg-indigo-100 rounded-full">
                  <FiPackage className="w-8 h-8 text-indigo-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Additional Info */}
        {stats && stats.averageDebtPerDebtor > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Average Metrics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Average Debt per Debtor</div>
                <div className="text-2xl font-bold text-gray-900">
                  BHD {stats.averageDebtPerDebtor.toFixed(3)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings */}
        {merchant.settings && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Merchant Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Threshold Amount</div>
                <div className="text-xl font-bold text-orange-600">
                  BHD {merchant.settings.thresholdAmount ? merchant.settings.thresholdAmount.toFixed(3) : '10.000'}
                </div>
                <div className="text-xs text-gray-500 mt-1">Alert when debt exceeds this amount</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default MerchantDetailsPage;
